import { useAppSSE } from 'contexts/AppSSEContext';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { VoiceProvider } from './VoiceContext';
import {
  addParticipant as apiAddParticipant,
  markAsRead as apiMarkAsRead,
  removeParticipant as apiRemoveParticipant,
  sendTypingIndicator as apiSendTyping,
  transferOwnership as apiTransferOwnership,
  updateParticipantRole as apiUpdateParticipantRole,
} from '../api/messaging.api';
import { useConversations } from '../hooks/useConversations';
import { useE2EE } from '../hooks/useE2EE';
import { useMessages } from '../hooks/useMessages';
import { getConversationDisplayName, getMessageSenderName } from '../utils/formatters';
import { resolveMessageSenderId } from '../utils/messengerConversationUtils';
import {
  playMessageNotificationSound,
  showMessengerNotification,
} from '../utils/messengerNotificationUtils';
import {
  readMessengerLastOpenedAtMs,
  resolveUnreadTotal,
  writeMessengerLastOpenedAtMs,
} from '../utils/messengerUnreadUtils';
import {
  clearMessengerDocumentTitle,
  setMessengerTitleUnreadCount,
} from '../utils/messengerTitleUtils';

const MessagingContext = createContext(null);

const noopAsyncNull = () => Promise.resolve(null);
const noopAsyncArray = () => Promise.resolve([]);
const noopAsyncFalse = () => Promise.resolve(false);
const noop = () => {};

const DISABLED_MESSAGING_CONTEXT = {
  isMessengerEnabled: false,
  conversations: [],
  selectedConversationId: null,
  selectedConversation: null,
  selectedMessages: [],
  messagesByConversation: {},
  typingUsers: {},
  totalUnreadCount: 0,
  currentUserId: null,
  conversationsLoading: false,
  messagesLoading: false,
  loadConversations: noopAsyncArray,
  createConversation: noopAsyncNull,
  updateConversation: noopAsyncNull,
  selectConversation: noop,
  loadMessages: noopAsyncArray,
  loadMessageContext: noopAsyncNull,
  sendMessage: noopAsyncNull,
  uploadMedia: noopAsyncNull,
  deleteMessage: noopAsyncFalse,
  editMessage: noopAsyncNull,
  toggleMessageReaction: noopAsyncNull,
  markAsRead: noopAsyncNull,
  setOpenConversationForUnreadSuppression: noop,
  requestMissingEncryptionKeys: noopAsyncArray,
  missingKeyConversationIds: [],
  keySyncStatusByConversation: {},
  keySyncReasonByConversation: {},
  keySyncError: null,
  clearSelectedConversationEncryptionKey: noopAsyncNull,
  sendTypingIndicator: noopAsyncNull,
  addParticipant: noopAsyncNull,
  updateParticipantRole: noopAsyncNull,
  transferOwnership: noopAsyncNull,
  removeParticipant: noopAsyncNull,
  updateConversationName: noopAsyncNull,
  leaveConversation: noopAsyncNull,
};

function MessagingCoordinator({ children, currentUserId }) {
  const { subscribe } = useAppSSE();
  const e2ee = useE2EE();
  const {
    isEnabled: e2eeIsEnabled,
    checkMissingKeys,
    requestMissingKeys,
    provideKeysForReencryptionRequest,
    hydrateAvailableKey,
    getCurrentDeviceId,
    shareConversationKeyWithUser,
    removeConversationKey,
    clearConversationKeyForTesting,
    createAndShareConversationKey,
    missingKeyConversationIds,
    keySyncStatusByConversation,
    keySyncReasonByConversation,
    keySyncError,
  } = e2ee || {};

  const {
    conversations,
    loading: conversationsLoading,
    loadConversations,
    createConversation,
    updateConversation,
    injectConversation,
  } = useConversations();

  const getConversationById = useCallback(
    (conversationId) =>
      conversations.find((conversation) => `${conversation?.id}` === `${conversationId}`) || null,
    [conversations],
  );

  const {
    messagesByConversation,
    loading: messagesLoading,
    loadMessages,
    loadMessageContext,
    sendMessage,
    uploadMedia,
    deleteMessage,
    editMessage,
    syncMessageReactions,
    toggleMessageReaction: toggleMessageReactionForConversation,
  } = useMessages({ e2ee, getConversationById });

  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [openConversationId, setOpenConversationId] = useState(null);
  const [lastOpenedAtMs, setLastOpenedAtMs] = useState(() => readMessengerLastOpenedAtMs());

  const touchMessengerOpenedAt = useCallback((timestampMs = Date.now()) => {
    const nextTimestampMs = Number.isFinite(timestampMs) ? timestampMs : Date.now();
    writeMessengerLastOpenedAtMs(nextTimestampMs);
    setLastOpenedAtMs(nextTimestampMs);
  }, []);

  // Initial load
  useEffect(() => {
    if (e2eeIsEnabled) {
      checkMissingKeys?.().catch(() => undefined);
    }
    loadConversations();
  }, [checkMissingKeys, e2eeIsEnabled, loadConversations]);

  // Load messages when conversation selected
  useEffect(() => {
    if (selectedConversationId) {
      loadMessages(selectedConversationId, {}, getConversationById(selectedConversationId));
    }
  }, [getConversationById, selectedConversationId, loadMessages]);

  // ── SSE handlers ─────────────────────────────────────────────────────────────
  // Server is source of truth — SSE events trigger a refetch rather than local patches

  useEffect(() => {
    const unsubConversationCreated = subscribe('conversation-created', (data) => {
      loadConversations();
    });

    const unsubParticipantJoined = subscribe('participant-joined', async (data) => {
      const conversationId = data?.conversationId;
      const userId = data?.userId;
      if (e2eeIsEnabled && conversationId != null && userId != null) {
        shareConversationKeyWithUser?.(conversationId, userId).catch(() => undefined);
      }
      loadConversations();
    });

    const unsubParticipantLeft = subscribe('participant-left', (data) => {
      if (
        e2eeIsEnabled &&
        data?.conversationId != null &&
        `${data?.userId}` === `${currentUserId}`
      ) {
        removeConversationKey?.(data.conversationId).catch(() => {});
      }
      loadConversations();
    });

    const unsubMessageSent = subscribe('message-sent', async (data) => {
      const { conversationId } = data || {};
      if (conversationId) {
        // Load messages first to get the latest message
        const messages = await loadMessages(
          conversationId,
          {},
          getConversationById(conversationId),
        );

        // Only reload conversations (which updates unread counts) if the message
        // is from someone else. This prevents showing notifications for your own messages.
        const latestMessage =
          messages && messages.length > 0 ? messages[messages.length - 1] : data?.message || null;
        const latestMessageSenderId =
          resolveMessageSenderId(latestMessage) ?? data?.userId ?? data?.senderId ?? null;
        const isOwnMessage =
          currentUserId != null &&
          latestMessageSenderId != null &&
          `${latestMessageSenderId}` === `${currentUserId}`;

        if (!isOwnMessage) {
          const isOpenConversation = `${conversationId}` === `${openConversationId}`;
          if (isOpenConversation) {
            touchMessengerOpenedAt();
            try {
              await apiMarkAsRead(conversationId);
            } catch (err) {
              console.error('[MessagingContext] auto markAsRead failed:', err);
            }
          } else if (latestMessage) {
            const conversation = getConversationById(conversationId);
            const senderName = getMessageSenderName(
              latestMessage,
              conversation?.participants || [],
            );
            const conversationName = getConversationDisplayName(conversation, currentUserId);
            const isGroup = conversation?.type === 'group';
            const title = isGroup
              ? `${senderName} in ${conversationName}`
              : `New message from ${senderName}`;
            const content =
              typeof latestMessage.content === 'string' && latestMessage.content.trim()
                ? latestMessage.content.trim()
                : latestMessage.content_type === 'image'
                  ? 'Sent an image'
                  : latestMessage.content_type === 'video'
                    ? 'Sent a video'
                    : 'New message';

            playMessageNotificationSound();
            showMessengerNotification({
              title,
              body: content,
              tag: `message:${conversationId}`,
            });
          }
          loadConversations();
        }
      }
    });

    const unsubMessageEdited = subscribe('message-edited', (data) => {
      const { message } = data || {};
      if (message?.conversation_id) {
        const conversationId = message.conversation_id;
        loadMessages(conversationId, {}, getConversationById(conversationId));
      }
    });

    const unsubMessageDeleted = subscribe('message-deleted', (data) => {
      const { conversationId } = data || {};
      if (conversationId) {
        loadMessages(conversationId, {}, getConversationById(conversationId));
      }
    });

    const unsubTypingStarted = subscribe('typing-started', (data) => {
      const { conversationId, userId } = data || {};
      if (conversationId && userId) {
        setTypingUsers((prev) => ({
          ...prev,
          [conversationId]: [...new Set([...(prev[conversationId] || []), userId])],
        }));
      }
    });

    const unsubTypingStopped = subscribe('typing-stopped', (data) => {
      const { conversationId, userId } = data || {};
      if (conversationId && userId) {
        setTypingUsers((prev) => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).filter((id) => `${id}` !== `${userId}`),
        }));
      }
    });

    const unsubMessagesRead = subscribe('messages-read', () => {
      loadConversations();
    });

    const unsubReactionAdded = subscribe('reaction-added', (data) => {
      const { conversationId, messageId } = data || {};
      if (conversationId && messageId != null) {
        syncMessageReactions(conversationId, messageId);
      }
    });

    const unsubReactionRemoved = subscribe('reaction-removed', (data) => {
      const { conversationId, messageId } = data || {};
      if (conversationId && messageId != null) {
        syncMessageReactions(conversationId, messageId);
      }
    });

    const unsubKeyReencryptionRequest = subscribe('key-reencryption-request', async (data) => {
      console.info('[[E2EE]] web SSE key sync request event received', {
        e2eeEnabled: e2eeIsEnabled,
        requestingDeviceId:
          data?.requestingDeviceId ?? data?.requesting_device_id ?? data?.targetDeviceId ?? data?.target_device_id ?? null,
        conversationIds: data?.conversationIds ?? data?.conversation_ids ?? [],
        requestingUserId: data?.userId ?? data?.user_id ?? null,
      });
      if (!e2eeIsEnabled) return;
      provideKeysForReencryptionRequest?.(data).catch((error) => {
        console.error('[MessagingContext] provide encryption keys failed:', error);
      });
    });

    const unsubKeyAvailable = subscribe('key-available', async (data) => {
      if (!e2eeIsEnabled) return;
      const conversationId = data?.conversationId;
      if (conversationId == null) return;

      try {
        const currentDeviceId = await getCurrentDeviceId?.();
        if (
          data?.targetDeviceId &&
          currentDeviceId &&
          `${data.targetDeviceId}` !== `${currentDeviceId}`
        ) {
          return;
        }

        const tryHydrate = async () => {
          const hydratedKey = await hydrateAvailableKey?.(conversationId);
          if (!hydratedKey) return false;
          const hasLoadedMessages = Boolean(messagesByConversation[conversationId]?.length);
          const isSelectedConversation = `${conversationId}` === `${selectedConversationId}`;
          if (hasLoadedMessages || isSelectedConversation) {
            await loadMessages(conversationId, {}, getConversationById(conversationId));
          }
          return true;
        };

        const synced = await tryHydrate();
        if (!synced) {
          [1500, 4000].forEach((delay) => {
            setTimeout(() => tryHydrate().catch(() => undefined), delay);
          });
        }
      } catch (error) {
        console.error('[MessagingContext] hydrate available encryption key failed:', error);
      }
    });

    return () => {
      unsubConversationCreated();
      unsubParticipantJoined();
      unsubParticipantLeft();
      unsubMessageSent();
      unsubMessageEdited();
      unsubMessageDeleted();
      unsubTypingStarted();
      unsubTypingStopped();
      unsubMessagesRead();
      unsubReactionAdded();
      unsubReactionRemoved();
      unsubKeyReencryptionRequest();
      unsubKeyAvailable();
    };
  }, [
    e2eeIsEnabled,
    getConversationById,
    getCurrentDeviceId,
    hydrateAvailableKey,
    subscribe,
    loadConversations,
    loadMessages,
    messagesByConversation,
    provideKeysForReencryptionRequest,
    removeConversationKey,
    shareConversationKeyWithUser,
    syncMessageReactions,
    currentUserId,
    openConversationId,
    selectedConversationId,
    touchMessengerOpenedAt,
  ]);

  // ── Actions ───────────────────────────────────────────────────────────────────

  const markAsRead = useCallback(
    async (conversationId) => {
      if (!conversationId) return;
      touchMessengerOpenedAt();
      try {
        await apiMarkAsRead(conversationId);
        loadConversations();
      } catch (err) {
        console.error('[MessagingContext] markAsRead failed:', err);
      }
    },
    [loadConversations, touchMessengerOpenedAt],
  );

  const setOpenConversationForUnreadSuppression = useCallback(
    (conversationId) => {
      if (!conversationId) {
        setOpenConversationId(null);
        return;
      }
      setOpenConversationId(conversationId);
      touchMessengerOpenedAt();
    },
    [touchMessengerOpenedAt],
  );

  const sendTypingIndicator = useCallback(async (conversationId, isTyping) => {
    try {
      await apiSendTyping(conversationId, { isTyping });
    } catch (err) {
      console.error('[MessagingContext] sendTypingIndicator failed:', err);
    }
  }, []);

  const updateParticipantRole = useCallback(
    async (conversationId, userId, role) => {
      try {
        await apiUpdateParticipantRole(conversationId, userId, { role });
        loadConversations(); // Refetch to get updated participant roles
      } catch (err) {
        console.error('[MessagingContext] updateParticipantRole failed:', err);
        throw err;
      }
    },
    [loadConversations],
  );

  const transferOwnership = useCallback(
    async (conversationId, newOwnerId) => {
      try {
        await apiTransferOwnership(conversationId, { newOwnerId });
        loadConversations(); // Refetch to get updated ownership
      } catch (err) {
        console.error('[MessagingContext] transferOwnership failed:', err);
        throw err;
      }
    },
    [loadConversations],
  );

  const addParticipant = useCallback(
    async (conversationId, userId, role = 'standard') => {
      try {
        await apiAddParticipant(conversationId, { userId, role });
        if (e2eeIsEnabled) {
          shareConversationKeyWithUser?.(conversationId, userId).catch(() => undefined);
        }
        loadConversations(); // Refetch to update participant list
      } catch (err) {
        console.error('[MessagingContext] addParticipant failed:', err);
        throw err;
      }
    },
    [e2eeIsEnabled, loadConversations, shareConversationKeyWithUser],
  );

  const removeParticipant = useCallback(
    async (conversationId, userId) => {
      try {
        await apiRemoveParticipant(conversationId, userId);
        loadConversations(); // Refetch to update participant list
      } catch (err) {
        console.error('[MessagingContext] removeParticipant failed:', err);
        throw err;
      }
    },
    [loadConversations],
  );

  const updateConversationName = useCallback(
    async (conversationId, name) => {
      try {
        await updateConversation(conversationId, { name });
        loadConversations(); // Refetch to get updated conversation
      } catch (err) {
        console.error('[MessagingContext] updateConversationName failed:', err);
        throw err;
      }
    },
    [updateConversation, loadConversations],
  );

  const leaveConversation = useCallback(
    async (conversationId) => {
      try {
        await apiRemoveParticipant(conversationId, currentUserId);
        if (e2eeIsEnabled) {
          removeConversationKey?.(conversationId).catch(() => {});
        }
        // Clear selection if leaving the currently selected conversation
        if (`${conversationId}` === `${selectedConversationId}`) {
          setSelectedConversationId(null);
        }
        loadConversations(); // Refetch to update conversation list
      } catch (err) {
        console.error('[MessagingContext] leaveConversation failed:', err);
        throw err;
      }
    },
    [
      currentUserId,
      e2eeIsEnabled,
      removeConversationKey,
      selectedConversationId,
      loadConversations,
    ],
  );

  const createConversationWithE2EE = useCallback(
    async (conversationPayload) => {
      const conversation = await createConversation(conversationPayload);
      if (!conversation) return conversation;

      if (e2eeIsEnabled) {
        try {
          await createAndShareConversationKey?.(conversation);
        } catch {
          // Intentionally do not block conversation creation on key distribution retries.
        }
      }
      return conversation;
    },
    [createAndShareConversationKey, createConversation, e2eeIsEnabled],
  );

  const selectConversation = useCallback(
    (conversationId) => {
      setSelectedConversationId(conversationId);
      if (conversationId) markAsRead(conversationId);
    },
    [markAsRead],
  );

  // ── Derived ───────────────────────────────────────────────────────────────────

  const clearSelectedConversationEncryptionKey = useCallback(async () => {
    if (!selectedConversationId || !e2eeIsEnabled) return null;
    await clearConversationKeyForTesting?.(selectedConversationId);
    await loadMessages(selectedConversationId);
    return selectedConversationId;
  }, [clearConversationKeyForTesting, e2eeIsEnabled, loadMessages, selectedConversationId]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => `${c.id}` === `${selectedConversationId}`) || null,
    [conversations, selectedConversationId],
  );

  const selectedMessages = useMemo(
    () => (selectedConversationId ? messagesByConversation[selectedConversationId] || [] : []),
    [selectedConversationId, messagesByConversation],
  );

  const totalUnreadCount = useMemo(
    () =>
      resolveUnreadTotal({
        conversations,
        currentUserId,
        lastOpenedAtMs,
      }),
    [conversations, currentUserId, lastOpenedAtMs],
  );

  useEffect(() => {
    setMessengerTitleUnreadCount(totalUnreadCount);
    return clearMessengerDocumentTitle;
  }, [totalUnreadCount]);

  const value = {
    isMessengerEnabled: true,
    // state
    conversations,
    selectedConversationId,
    selectedConversation,
    selectedMessages,
    messagesByConversation,
    typingUsers,
    totalUnreadCount,
    currentUserId,
    // loading
    conversationsLoading,
    messagesLoading,
    // actions
    loadConversations,
    updateConversation,
    selectConversation,
    loadMessages,
    createConversation: createConversationWithE2EE,
    loadMessageContext: (messageId, params) =>
      loadMessageContext(
        selectedConversationId,
        messageId,
        params,
        getConversationById(selectedConversationId),
      ),
    sendMessage: (content, replyToMessageId) =>
      sendMessage(
        selectedConversationId,
        content,
        currentUserId,
        replyToMessageId,
        getConversationById(selectedConversationId),
      ),
    uploadMedia: (file, caption) =>
      uploadMedia(selectedConversationId, file, caption, currentUserId),
    deleteMessage: (messageId) => deleteMessage(selectedConversationId, messageId),
    editMessage: (messageId, content) => editMessage(selectedConversationId, messageId, content),
    toggleMessageReaction: (messageId, reactionType) =>
      selectedConversationId
        ? toggleMessageReactionForConversation(
            selectedConversationId,
            messageId,
            reactionType,
            currentUserId,
          )
        : Promise.resolve(null),
    markAsRead,
    setOpenConversationForUnreadSuppression,
    requestMissingEncryptionKeys: requestMissingKeys || noopAsyncArray,
    missingKeyConversationIds: missingKeyConversationIds || [],
    keySyncStatusByConversation: keySyncStatusByConversation || {},
    keySyncReasonByConversation: keySyncReasonByConversation || {},
    keySyncError: keySyncError || null,
    clearSelectedConversationEncryptionKey,
    sendTypingIndicator,
    // participant management
    addParticipant: (userId, role) =>
      selectedConversationId
        ? addParticipant(selectedConversationId, userId, role)
        : Promise.reject(new Error('No conversation selected')),
    updateParticipantRole: (userId, role) =>
      selectedConversationId
        ? updateParticipantRole(selectedConversationId, userId, role)
        : Promise.reject(new Error('No conversation selected')),
    transferOwnership: (newOwnerId) =>
      selectedConversationId
        ? transferOwnership(selectedConversationId, newOwnerId)
        : Promise.reject(new Error('No conversation selected')),
    removeParticipant: (userId) =>
      selectedConversationId
        ? removeParticipant(selectedConversationId, userId)
        : Promise.reject(new Error('No conversation selected')),
    // conversation management
    updateConversationName: (name) =>
      selectedConversationId
        ? updateConversationName(selectedConversationId, name)
        : Promise.reject(new Error('No conversation selected')),
    leaveConversation: () =>
      selectedConversationId
        ? leaveConversation(selectedConversationId)
        : Promise.reject(new Error('No conversation selected')),
    injectConversation,
  };

  return (
    <MessagingContext.Provider value={value}>
      <VoiceProvider currentUserId={currentUserId}>{children}</VoiceProvider>
    </MessagingContext.Provider>
  );
}

export function MessagingProvider({ currentUserId, children }) {
  return <MessagingCoordinator currentUserId={currentUserId}>{children}</MessagingCoordinator>;
}

export function useMessaging() {
  const ctx = useContext(MessagingContext);
  return ctx || DISABLED_MESSAGING_CONTEXT;
}
